const { formatMoney } = window.BAO.Utils

class ProductStones extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.selectedData = {
      stoneSetting: null,
      stoneSettingHandle: null,
      stoneType: null,
      stoneTypeHandle: null,
      stoneQuantity: this.dataset.stoneQuantityDefault || null,
      variantId: null,
      variantPrice: null,
    }
    this.selectedDataComplete = false

    // Data
    this.variantQuantities = JSON.parse(this.dataset.stoneInventoryData) || {}

    this.inputEls = this.els.input?.elements
    this.labelEls = this.els.label?.elements
    this.stoneTypeInputEls = this.els['stone-type-input']?.elements
    this.stoneSettingInputEls = this.els['stone-setting-input']?.elements
    this.stoneQuantityInputEls = this.els['stone-quantity-input']?.elements

    // Product form elements els
    this.productImageEls = this.externalEls['product-image']?.elements

    // Customer facing property els
    this.messagePropertyEl = this.externalEls['message-property']

    // Reset form button
    this.formResetButtonEl = this.externalEls['form-reset']?.element

    // Hidden property els
    this.hasStonesPropertyEl =
      this.externalEls['has-stones-property']?.elements
    this.stoneTypePropertyEl =
      this.externalEls['stone-type-property']?.elements
    this.stoneSettingPropertyEl =
      this.externalEls['stone-setting-property']?.elements
    this.stoneQuantityPropertyEl =
      this.externalEls['stone-quantity-property']?.elements
    this.stoneVariantIdPropertyEl =
      this.externalEls['stone-variant-id-property']?.elements
    this.identifierPropertyEl =
      this.externalEls['stone-identifier-property']?.elements

    // Engraving tab / button
    this.personalisationButtonEls =
      this.externalEls['personalisation-button']?.elements
  }

  setupListeners () {
    super.setupListeners()

    this.inputEls?.forEach((input) => {
      input.addEventListener('click', (e) => this.handleInputClick(input))
      input.addEventListener('mouseenter', (e) =>
        this.handleInputMouseIn(input),
      )
      input.addEventListener('mouseout', (e) =>
        this.handleInputMouseOut(input),
      )
    })

    const resetButtons = [
      ...(this.els?.reset?.elements ?? []),
      ...(this.externalEls?.reset?.elements ?? []),
    ]
    resetButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.reset()
      })
    })

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PDP.PRICE_CHANGE,
      this.onPriceChange.bind(this),
    )
  }

  updateUUID () {
    this.identifierPropertyEl.forEach((el) => {
      el.value = window.BAO.UUID.v4()
    })
  }

  onPriceChange () {
    if (!this.selectedDataComplete) return

    this.updatePrices(
      this.selectedData.variantPrice,
      this.selectedData.stoneQuantity,
    )
  }

  handleInputMouseIn (input) {
    const inputType = input.dataset.inputType
    const value = input.value

    if (inputType && value) {
      const label = this.els.label?.elements.find(
        (el) => el.dataset.labelType === inputType,
      )

      if (label) {
        if (!label.dataset.originalCurrentText) {
          label.dataset.originalCurrentText = label.dataset.currentText
        }
        label.innerText = value
      }
    }
  }

  handleInputMouseOut (input) {
    const inputType = input.dataset.inputType
    const value = input.value

    if (inputType && value) {
      const label = this.els.label?.elements.find(
        (el) => el.dataset.labelType === inputType,
      )

      if (label) {
        const originalCurrentText = label.dataset.originalCurrentText
        const currentText = label.dataset.currentText

        if (originalCurrentText && originalCurrentText === currentText) {
          label.innerText = originalCurrentText
        } else {
          label.innerText = currentText
        }

        // Clean up the temporary storage
        delete label.dataset.originalCurrentText
      }
    }
  }

  handleInputClick (input) {
    const { inputType } = input.dataset

    const userSelecting = !input.hasAttribute('aria-selected')

    this.updateLabels(input, userSelecting)

    // Hide error message for the specific option being selected
    if (userSelecting) {
      this.hideErrorForInputType(inputType)
    }

    switch (inputType) {
      case 'stone-setting':
        this.handleStoneSettingClick(input, userSelecting)
        break

      case 'stone-type':
        this.handleStoneTypeClick(input, userSelecting)
        break

      case 'stone-quantity':
        this.handleStoneQuantityClick(input, userSelecting)
        break

      default:
        console.warn(`Unknown input type: ${inputType}`)
    }

    this.checkSelection()
  }

  handleStoneSettingClick (input, userSelecting) {
    const isEngravedOption = input.dataset.isEngravedOption === 'true'

    this.updateSelectedInputs(this.stoneSettingInputEls, input, userSelecting)

    if (isEngravedOption === true) {
      this.handlePlainOptionSelect(false, userSelecting)
      this.reset()
      return
    }

    const { stoneSetting, stoneSettingHandle } = input.dataset
    const isPlainOption = input.dataset.isPlainOption === 'true'

    this.handlePlainOptionSelect(isPlainOption, userSelecting)

    this.selectedData.stoneSetting =
      userSelecting && !isPlainOption ? stoneSetting : null
    this.selectedData.stoneSettingHandle =
      userSelecting && !isPlainOption ? stoneSettingHandle : null
  }

  handleStoneTypeClick (input, userSelecting) {
    this.updateSelectedInputs(this.stoneTypeInputEls, input, userSelecting)

    const { stoneType, stoneTypeHandle, variantId, variantPrice } =
      input.dataset
    const isPlainOption = input.dataset.isPlainOption === 'true'

    this.selectedData.stoneType = userSelecting ? stoneType : null
    this.selectedData.stoneTypeHandle = userSelecting ? stoneTypeHandle : null
    this.selectedData.variantId = userSelecting ? variantId : null
    this.selectedData.variantPrice = userSelecting ? variantPrice : null

    if (isPlainOption) {
      this.handlePlainOptionSelectForStoneType(userSelecting)
    } else {
      if (this.dataset.hasQuantityOption === 'true') {
        this.checkStoneQuantities(variantId)
      }

      if (this.els['stone-quantity-form-option']?.element) {
        this.els['stone-quantity-form-option'].element.setAttribute(
          'aria-hidden',
          false,
        )
      }
    }
  }

  handleStoneQuantityClick (input, userSelecting) {
    this.updateSelectedInputs(this.stoneQuantityInputEls, input, userSelecting)

    const { stoneQuantity } = input.dataset

    this.selectedData.stoneQuantity = userSelecting ? stoneQuantity : null
  }

  handlePlainOptionSelect (isPlainOption = false, userSelecting = false) {
    let shouldHideInputs = false

    if (userSelecting === true && isPlainOption === true) {
      shouldHideInputs = true

      this.slideToFirstImage()
    } else if (
      (userSelecting === false && isPlainOption === true) ||
      (userSelecting === true && isPlainOption === false)
    ) {
      shouldHideInputs = false
    }

    if (this.els['stone-type-form-option']?.element) {
      this.els['stone-type-form-option'].element.setAttribute(
        'aria-hidden',
        shouldHideInputs,
      )
    }

    if (this.els['stone-quantity-form-option']?.element) {
      this.els['stone-quantity-form-option'].element.setAttribute(
        'aria-hidden',
        shouldHideInputs,
      )
    }

    if (shouldHideInputs) {
      this.selectedData.stoneQuantity = null
      this.selectedData.stoneType = null

      this.updateSelectedInputs(this.stoneTypeInputEls, false)
      this.updateSelectedInputs(this.stoneQuantityInputEls, false)

      this.resetButtons()
      this.resetPrices()
      this.resetLineItemProperties()
    }

    this.triggerFormValidationUpdate()
  }

  handlePlainOptionSelectForStoneType (userSelecting = false) {
    let shouldHideQuantity = false

    if (userSelecting === true) {
      shouldHideQuantity = true
    }

    if (this.els['stone-quantity-form-option']?.element) {
      this.els['stone-quantity-form-option'].element.setAttribute(
        'aria-hidden',
        shouldHideQuantity,
      )
    }

    if (shouldHideQuantity) {
      this.selectedData.stoneQuantity = null
      this.updateSelectedInputs(this.stoneQuantityInputEls, false)

      // Reset quantity label
      const quantityLabel = this.els.label?.elements.find(
        (el) => el.dataset.labelType === 'stone-quantity',
      )
      if (quantityLabel) {
        this.resetLabels([quantityLabel])
      }
    }

    this.triggerFormValidationUpdate()
  }

  checkStoneQuantities (variantId) {
    const variantInventoryData = this.variantQuantities[variantId]

    if (!variantInventoryData) return

    this.stoneQuantityInputEls.forEach((input) => {
      input.disabled =
        parseInt(input.value, 10) >
          parseInt(variantInventoryData.inventoryquantity, 10) &&
        variantInventoryData.inventorypolicy === 'deny'

      if (input.disabled && input.hasAttribute('aria-selected')) {
        input.removeAttribute('aria-selected')

        this.selectedData.stoneQuantity = null

        const quantityLabel = this.els.label?.elements.find(
          (el) => el.dataset.labelType === 'stone-quantity',
        )

        if (quantityLabel) {
          this.resetLabels([quantityLabel])
        }
      }
    })
  }

  checkSelection () {
    const hasSettingsOption = this.dataset.hasSettingsOption === 'true'
    const hasQuantityOption = this.dataset.hasQuantityOption === 'true'

    const stoneTypeFormOption = this.els['stone-type-form-option']?.element
    const stoneQuantityFormOption =
      this.els['stone-quantity-form-option']?.element

    const isStoneTypeHidden =
      stoneTypeFormOption?.getAttribute('aria-hidden') === 'true'
    const isStoneQuantityHidden =
      stoneQuantityFormOption?.getAttribute('aria-hidden') === 'true'

    this.selectedDataComplete = Object.entries(this.selectedData).every(
      ([key, value]) => {
        // Ignore stoneSetting/stoneSettingHandle if no settings option
        if (
          !hasSettingsOption &&
          (key === 'stoneSetting' || key === 'stoneSettingHandle')
        ) {
          return true
        }

        // Ignore stoneType/stoneTypeHandle if stone type is hidden
        if (
          isStoneTypeHidden &&
          (key === 'stoneType' ||
            key === 'stoneTypeHandle' ||
            key === 'variantId' ||
            key === 'variantPrice')
        ) {
          return true
        }

        // Ignore stoneQuantity if no quantity option or if quantity is hidden
        if (
          (!hasQuantityOption || isStoneQuantityHidden) &&
          key === 'stoneQuantity'
        ) {
          return true
        }

        const personalisationFormEl = document.querySelector(
          'personalisation-form',
        )

        if (personalisationFormEl) {
          personalisationFormEl.setAttribute(
            'selected-stone-type',
            this.selectedData.stoneTypeHandle,
          )
        }

        window.BAO.dispatchEvent(window.BAO.EVENTS.PDP.OPTION_CHANGED, {
          stone: this.selectedData.stoneTypeHandle,
        })

        // All other values must not be null or undefined
        return value !== null && value !== undefined
      },
    )

    if (!this.selectedDataComplete) {
      this.resetButtons()
      this.resetPrices()
      this.resetLineItemProperties()

      this.triggerFormValidationUpdate()
      return
    }

    if (this.selectedData.stoneQuantity === null) {
      this.selectedData.stoneQuantity = '1'
    }

    this.handleFinalSelection()
  }

  handleFinalSelection () {
    this.updateProductImagery(
      this.selectedData.stoneSettingHandle,
      this.selectedData.stoneTypeHandle,
      this.selectedData.stoneQuantity,
    )

    this.updatePrices(
      this.selectedData.variantPrice,
      this.selectedData.stoneQuantity,
    )

    this.updateLineItemProperties(
      this.selectedData.stoneSetting,
      this.selectedData.stoneType,
      this.selectedData.stoneQuantity,
      this.selectedData.variantId,
      this.selectedData.variantPrice,
    )

    if (
      this.personalisationButtonEls &&
      this.dataset.allowPersonalisation !== 'true'
    ) {
      this.personalisationButtonEls.forEach((el) => {
        el.setAttribute('disabled', true)
      })
    }

    if (this.formResetButtonEl) {
      this.formResetButtonEl.removeAttribute('disabled')
    }

    this.updateAddToCartButtonText(true)
  }

  updateProductImagery (stoneSettingHandle, stoneTypeHandle, stoneQuantity) {
    if (!this.productImageEls) return

    let matchingImage
    let currentQuantity = parseInt(stoneQuantity, 10)

    while (currentQuantity >= 1 && !matchingImage) {
      matchingImage = this.productImageEls.find((image) => {
        const typeMatch = image.dataset.stoneTypeHandle === stoneTypeHandle
        const quantityMatch =
          parseInt(image.dataset.stoneQuantity, 10) === currentQuantity
        const settingMatch = stoneSettingHandle
          ? image.dataset.stoneSettingHandle === stoneSettingHandle
          : true

        return typeMatch && quantityMatch && settingMatch
      })

      if (!matchingImage) currentQuantity--
    }

    if (matchingImage && matchingImage.dataset.index) {
      document
        .querySelector('product-media')
        ?.moveToIdx(matchingImage.dataset.index)
    }
  }

  slideToFirstImage () {
    const productMediaEl = document.querySelector('product-media')
    if (productMediaEl && productMediaEl.moveToIdx) {
      productMediaEl.moveToIdx(0)
    }
  }

  updatePrices (variantPrice, stoneQuantity) {
    if (!variantPrice || !stoneQuantity) {
      console.warn('Missing data for price update')
      this.reset()

      return
    }

    const productPriceEls = document.querySelectorAll(
      this.productPriceSelector,
    )

    productPriceEls.forEach((priceEl) => {
      // Regular price elements are only present when an item is on sale and has a compare-at price
      const regularPriceEl = priceEl.querySelector(this.priceRegularSelector)
      const priceTextEl = priceEl.querySelector('[data-price]')

      // Skip if no price text element is found
      if (!priceTextEl) return

      // Prices
      let originalPrice
      if (priceEl.hasAttribute('data-personalisation-form-el')) {
        originalPrice = parseInt(
          priceTextEl.dataset.originalPrice || priceTextEl.dataset.price,
        )
      } else {
        originalPrice = parseInt(priceTextEl.dataset.price)
      }

      const stonesPrice =
        parseInt(variantPrice, 10) * parseInt(stoneQuantity, 10)
      const newPrice = originalPrice + stonesPrice

      priceTextEl.innerText = formatMoney(newPrice)
      priceEl.dataset.stonesPrice = stonesPrice

      if (regularPriceEl) {
        regularPriceEl.setAttribute('hidden', true)
      }
    })
  }

  updateLineItemProperties (stoneSetting, stoneType, stoneQuantity, variantId) {
    let messagePropertyValue =
      this.messagePropertyEl?.element.dataset?.valueTemplate

    const stoneSettingMessage = stoneSetting
      ? `${window.theme.strings.stoneCustomisationSettingPrefix} ${stoneSetting}`
      : ''
    const stoneTypeMessage = stoneType
      ? `${window.theme.strings.stoneCustomisationTypePrefix} ${stoneType}`
      : ''
    const stoneQuantityMessage = stoneQuantity
      ? `${window.theme.strings.stoneCustomisationQuantityPrefix} ${stoneQuantity}`
      : ''

    messagePropertyValue = messagePropertyValue
      .replace('[setting]', stoneSettingMessage)
      .replace('[type]', stoneTypeMessage)
      .replace('[quantity]', stoneQuantityMessage)
      .replace(/ {2}/g, ' ')
      .trim()

    this.messagePropertyEl?.elements.forEach((el) => {
      // Customer facing property
      el.value = messagePropertyValue || 'true'
      el.removeAttribute('disabled')
    })

    // Hidden properties
    this.hasStonesPropertyEl.forEach((el) => {
      el.value = 'true'
      el.removeAttribute('disabled')
    })

    this.stoneTypePropertyEl.forEach((el) => {
      el.value = stoneType
      el.removeAttribute('disabled')
    })

    if (stoneSetting !== null) {
      this.stoneSettingPropertyEl.forEach((el) => {
        el.value = stoneSetting
        el.removeAttribute('disabled')
      })
    }

    this.stoneQuantityPropertyEl.forEach((el) => {
      el.value = stoneQuantity || '1'
      el.removeAttribute('disabled')
    })

    this.stoneVariantIdPropertyEl.forEach((el) => {
      el.value = variantId
      el.removeAttribute('disabled')
    })

    this.identifierPropertyEl.forEach((el) => {
      el.value = window.BAO.UUID.v4()
      el.removeAttribute('disabled')
    })
  }

  hideErrorForInputType (inputType) {
    // Check if engraving tab is currently active
    const engravingTabActive =
      document.querySelector(
        '[data-personalisation-tabs-el="tab"][aria-controls="panel-add-personalisation"][aria-selected="true"]',
      ) !== null

    // Don't hide errors if engraving tab is active
    if (engravingTabActive) {
      return
    }

    let errorElement = null

    switch (inputType) {
      case 'stone-setting':
        errorElement = this.els['stone-setting-error']?.element
        break
      case 'stone-type':
        errorElement = this.els['stone-type-error']?.element
        break
      case 'stone-quantity':
        errorElement = this.els['stone-quantity-error']?.element
        break
    }

    if (errorElement) {
      errorElement.setAttribute('aria-hidden', 'true')
    }
  }

  triggerFormValidationUpdate () {
    const productFormEl = document.querySelector(
      `[data-form-id="${this.dataset.productFormId}"]`,
    )
    if (productFormEl && productFormEl.checkStoneSelectionValid) {
      const stoneTypeError = this.els['stone-type-error']?.element
      const stoneSettingError = this.els['stone-setting-error']?.element

      if (stoneTypeError) stoneTypeError.setAttribute('aria-hidden', 'true')
      if (stoneSettingError) {
        stoneSettingError.setAttribute('aria-hidden', 'true')
      }

      productFormEl.checkStoneSelectionValid()
    }
  }

  resetSelectedData () {
    this.selectedData = {
      stoneSetting: null,
      stoneSettingHandle: null,
      stoneType: null,
      stoneTypeHandle: null,
      stoneQuantity: null,
      variantId: null,
      variantPrice: null,
    }

    // Prices & properties
    this.resetPrices()
    this.resetLineItemProperties()
  }

  resetPrices () {
    const productPriceEls = document.querySelectorAll(
      this.productPriceSelector,
    )

    productPriceEls.forEach((priceEl) => {
      // Regular price elements are only present when an item is on sale and has a compare-at price
      const regularPriceEl = priceEl.querySelector(this.priceRegularSelector)
      const priceTextEl = priceEl.querySelector('[data-price]')

      // Skip if no price text element is found
      if (!priceTextEl) return

      // Prices
      const originalPrice = parseInt(priceTextEl.dataset.price)
      priceTextEl.innerText = formatMoney(originalPrice)
      priceEl.dataset.stonesPrice = 0

      if (regularPriceEl) {
        regularPriceEl.removeAttribute('hidden')
      }
    })
  }

  resetLineItemProperties () {
    // Customer facing property
    if (this.messagePropertyEl.exists) {
      this.messagePropertyEl.elements.forEach((el) => {
        el.disabled = true
      })
    }

    // Hidden properties
    if (this.hasStonesPropertyEl.exists) {
      this.hasStonesPropertyEl.elements.forEach((el) => {
        el.disabled = true
      })
    }

    if (this.stoneTypePropertyEl.exists) {
      this.stoneTypePropertyEl.elements.forEach((el) => {
        el.disabled = true
      })
    }

    if (this.stoneSettingPropertyEl.exists) {
      this.stoneSettingPropertyEl.elements.forEach((el) => {
        el.disabled = true
      })
    }

    if (this.stoneQuantityPropertyEl.exists) {
      this.stoneQuantityPropertyEl.elements.forEach((el) => {
        el.disabled = true
      })
    }

    if (this.stoneVariantIdPropertyEl.exists) {
      this.stoneVariantIdPropertyEl.elements.forEach((el) => {
        el.disabled = true
      })
    }

    if (this.identifierPropertyEl.exists) {
      this.identifierPropertyEl.elements.forEach((el) => {
        el.disabled = true
      })
    }
  }

  resetLabels (labelArray) {
    if (!labelArray) return

    labelArray.forEach((labelEl) => {
      labelEl.dataset.currentText = labelEl.dataset.defaultText
      labelEl.innerText = labelEl.dataset.defaultText
    })
  }

  updateSelectedInputs (inputArray, input, userSelecting) {
    if (!inputArray) return

    inputArray.forEach((inputEl) => {
      if (input && input === inputEl && userSelecting === true) {
        inputEl.setAttribute('aria-selected', true)
      } else {
        inputEl.removeAttribute('aria-selected')
      }
    })
  }

  updateLabels (input, userSelecting) {
    const inputType = input.dataset.inputType
    const value = input.value

    if (inputType && value) {
      const label = this.els.label?.elements.find(
        (el) => el.dataset.labelType === inputType,
      )

      if (!label) return

      if (userSelecting === true) {
        label.dataset.currentText = value
        label.innerText = value
      } else {
        label.dataset.currentText = label.dataset.defaultText
      }
    }
  }

  resetButtons () {
    if (this.personalisationButtonEls) {
      this.personalisationButtonEls.forEach((el) => {
        el.removeAttribute('disabled')
      })
    }

    if (this.formResetButtonEl) {
      this.formResetButtonEl.setAttribute('disabled', true)
    }

    this.updateAddToCartButtonText(false)
  }

  reset () {
    this.resetSelectedData()

    this.resetLabels(this.els.label?.elements)

    // Reset inputs
    this.updateSelectedInputs(this.stoneSettingInputEls, false)
    this.updateSelectedInputs(this.stoneTypeInputEls, false)
    this.updateSelectedInputs(this.stoneQuantityInputEls, false)

    const mediaEl = document.querySelector('product-media')
    mediaEl.moveToIdx(0)

    // Reset personalisation buttons
    this.resetButtons()
  }

  get inputUnselectedClass () {
    return `prd-Form_VariantText-light`
  }

  get finalSelectionAttribute () {
    return `data-final-selection`
  }

  get productPriceSelector () {
    return `[data-form-id^="product-form-template-"] [data-product-price]`
  }

  get priceRegularSelector () {
    return `.prd-Price_RegularPrice`
  }

  updateAddToCartButtonText (stoneSelected) {
    const el = document.querySelector(
      '[data-product-personalisation-add-to-cart-text]',
    )
    if (!el) return

    el.dataset.stoneSelected = stoneSelected ? 'true' : 'false'

    el.textContent = stoneSelected
      ? window.theme.strings.addToCart
      : window.theme.strings.addToCartPersonalisation
  }
}

if (!customElements.get('product-stones')) {
  customElements.define('product-stones', ProductStones)
}
